import { useMemo, useState } from 'react';
import { Card, Button, Input, Tag, Space, Typography, Select, Progress, Tooltip, Badge, Popconfirm, message, theme } from '../../ui';
import type { TagTone } from '../../ui';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Plus, Search, Phone, MessageCircle,
  MapPin, Eye, Download, Upload, Pencil, Eraser, Merge,
  Ban, CheckCircle2,
} from 'lucide-react';
import { request, api } from '../../api/client';
import { usePermissionChecker } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import { RetailerFormModal } from './RetailerFormModal';
import { BulkImportModal } from '../../components/BulkImportModal';
import { TemplateButton } from '../../components/TemplateButton';
import { MergeCustomerModal } from '../territory/MergeCustomerModal';
import { displayMobile, waNumber } from '../../lib/validators';
import { formatIstDate } from '../../lib/time';
import { STATE_NAME } from '../../constants/indianStates';

const STATUS_COLOR: Record<string, TagTone> = {
  ACTIVE: 'green', KYC_PENDING: 'gold', PROSPECT: 'blue', SUSPENDED: 'red', CLOSED: 'neutral',
};
const STATE_LABEL = STATE_NAME;
const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const opt = (arr: any[]) => (arr ?? []).map((o: any) => ({ value: o.value, label: `${o.label}${o.count != null ? ` (${o.count})` : ''}` }));

// Dealer-agreement status from its expiry date. "alert" rows are tinted red in the
// table so expiring/expired agreements stand out. Threshold: 30 days.
const AGREEMENT_WARN_DAYS = 30;
function agreementStatus(expiry?: string | null): { key: string; label: string; color: TagTone; alert: boolean } {
  if (!expiry) return { key: 'none', label: 'No agreement', color: 'neutral', alert: false };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiry); exp.setHours(0, 0, 0, 0);
  const days = Math.round((exp.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { key: 'expired', label: `Expired ${-days}d ago`, color: 'red', alert: true };
  if (days <= AGREEMENT_WARN_DAYS) return { key: 'expiring', label: `Expires in ${days}d`, color: 'gold', alert: true };
  return { key: 'active', label: 'Active', color: 'green', alert: false };
}

export function RetailersListPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const hasPerm = usePermissionChecker();
  const canCreate = hasPerm('RETAILER.CREATE');
  const canEdit = hasPerm('RETAILER.EDIT');
  const canExport = hasPerm('RETAILER.EXPORT');
  const canImport = hasPerm('RETAILER.IMPORT');
  const canMerge = hasPerm('TERRITORY.SPLIT_MERGE');
  const canStatus = hasPerm('RETAILER.STATUS'); // activate/deactivate — Admin / Super Admin only
  const [mergingDup, setMergingDup] = useState<any | null>(null);
  const qc = useQueryClient();
  const statusMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      request({ url: `/retailers/${id}/status`, method: 'PATCH', data: { active } }),
    onSuccess: (_d, v) => {
      message.success(v.active ? 'Retailer activated' : 'Retailer deactivated');
      qc.invalidateQueries({ queryKey: ['retailers'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to change status'),
  });

  // ---- Filters (server-side) ----
  const [statusF, setStatusF] = useState<string[]>([]);
  const [territoryF, setTerritoryF] = useState<string[]>([]);
  const [salesF, setSalesF] = useState<string[]>([]);
  const [stateF, setStateF] = useState<string[]>([]);
  const [cityF, setCityF] = useState<string[]>([]);
  const [creditF, setCreditF] = useState<string[]>([]);
  const [hasOutstanding, setHasOutstanding] = useState(false);

  const params = useMemo(() => {
    const p: Record<string, any> = { page, pageSize };
    if (search.trim()) p.search = search.trim();
    if (statusF.length) p.status = statusF.join(',');
    if (territoryF.length) p.territoryId = territoryF.join(',');
    if (salesF.length) p.assignedSalesPersonId = salesF.join(',');
    if (stateF.length) p.stateCode = stateF.join(',');
    if (cityF.length) p.city = cityF.join(',');
    if (creditF.length) p.creditPeriodDays = creditF.join(',');
    if (hasOutstanding) p.hasOutstanding = 'true';
    return p;
  }, [page, pageSize, search, statusF, territoryF, salesF, stateF, cityF, creditF, hasOutstanding]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['retailers', params],
    queryFn: () => request<any>({ url: '/retailers', params }),
    placeholderData: keepPreviousData,
  });
  const { data: facets } = useQuery({
    queryKey: ['retailers', 'filter-options'],
    queryFn: () => request<any>({ url: '/retailers/filter-options' }),
  });
  const { data: territories } = useQuery({
    queryKey: ['territories-list'],
    queryFn: () => request<any[]>({ url: '/territories' }),
  });
  const { data: salesUsers } = useQuery({
    queryKey: ['sales-persons-list'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
  });

  const stateOpts = useMemo(() => (facets?.stateCode ?? []).map((s: any) => ({ value: s.value, label: `${STATE_LABEL.get(s.value) ?? s.value} (${s.count})` })), [facets]);
  const creditOpts = useMemo(() => (facets?.creditPeriodDays ?? []).map((c: any) => ({ value: c.value, label: `${c.value} days (${c.count})` })), [facets]);

  const activeFilters = statusF.length + territoryF.length + salesF.length + stateF.length + cityF.length + creditF.length + (hasOutstanding ? 1 : 0) + (search ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStatusF([]); setTerritoryF([]); setSalesF([]); setStateF([]); setCityF([]); setCreditF([]); setHasOutstanding(false); setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/retailers/export.csv', { responseType: 'blob', params });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'retailers.csv'; a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'code', title: 'Code', dataIndex: 'code', width: 120, sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => <Link to={`/retailers/${r.id}`} onClick={(e) => e.stopPropagation()}>{v}</Link> },
    {
      key: 'companyName', title: 'Company', dataIndex: 'companyName', width: 220, sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <Tooltip label={`${v}${r.contactPerson ? ` · ${r.contactPerson}` : ''}`}>
          <div style={{ maxWidth: 200 }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
            <div style={{ fontSize: 12, color: token.colorTextTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.contactPerson}</div>
          </div>
        </Tooltip>
      ),
    },
    { key: 'mobile', title: 'Mobile', dataIndex: 'mobile', width: 140,
      render: (v: string) => v && <a href={`tel:${displayMobile(v)}`} onClick={(e) => e.stopPropagation()}>{displayMobile(v)}</a> },
    { key: 'location', title: 'Location', dataIndex: 'city', width: 200, sortable: true, ellipsis: true, alwaysVisible: true,
      render: (city: string, r: any) => {
        const state = r.stateCode ? (STATE_LABEL.get(r.stateCode) ?? r.stateCode) : null;
        const txt = [city, state].filter(Boolean).join(' · ');
        return txt || <span style={{ color: '#bbb' }}>—</span>;
      } },
    { key: 'status', title: 'Status', dataIndex: 'status', width: 120, sortable: true,
      render: (s: string) => <Tag tone={STATUS_COLOR[s] ?? 'neutral'}>{s}</Tag> },
    {
      key: 'creditUsage', title: 'Credit Usage', width: 200,
      sorter: (a: any, b: any) => {
        const ua = Number(a.creditLimit) > 0 ? Number(a.outstandingBalance) / Number(a.creditLimit) : 0;
        const ub = Number(b.creditLimit) > 0 ? Number(b.outstandingBalance) / Number(b.creditLimit) : 0;
        return ua - ub;
      },
      render: (_: any, r: any) => {
        const limit = Number(r.creditLimit) || 0;
        const out = Number(r.outstandingBalance) || 0;
        const pct = limit > 0 ? Math.min(100, Math.round((out / limit) * 100)) : 0;
        const strokeColor = pct >= 90 ? '#D9363E' : pct >= 60 ? '#E7B22C' : '#52C41A';
        return (
          <Tooltip block label={`Outstanding ${inr2(out)} of ${inr2(limit)} · available ${inr2(limit - out)}`}>
            <div className="w-full">
              <Progress percent={pct} size="small" strokeColor={strokeColor} showInfo={false} />
              <div style={{ fontSize: 11, color: token.colorTextSecondary }}>{inr2(out)} / {inr2(limit)}</div>
            </div>
          </Tooltip>
        );
      },
    },
    { key: 'creditLimit', title: 'Credit Limit', dataIndex: 'creditLimit', width: 130, align: 'right', sortable: true, defaultVisible: false, render: (v: any) => inr2(v) },
    { key: 'outstandingBalance', title: 'Outstanding', dataIndex: 'outstandingBalance', width: 140, align: 'right', sortable: true, defaultVisible: false,
      render: (v: any) => <span style={{ color: Number(v) > 0 ? '#D9363E' : undefined }}>{inr2(v)}</span> },
    { key: 'creditPeriodDays', title: 'Credit Days', dataIndex: 'creditPeriodDays', width: 110, align: 'right', defaultVisible: false },
    { key: 'gstNumber', title: 'GST', dataIndex: 'gstNumber', width: 170, ellipsis: true, defaultVisible: false },
    { key: 'agriLicenseNo', title: 'Agri License No.', dataIndex: 'agriLicenseNo', width: 180, ellipsis: true, alwaysVisible: true,
      render: (v: string) => v || <span style={{ color: token.colorTextTertiary }}>—</span> },
    { key: 'pincode', title: 'Pincode', dataIndex: 'pincode', width: 100, defaultVisible: false },
    { key: 'email', title: 'Email', dataIndex: 'email', width: 200, ellipsis: true, defaultVisible: false,
      render: (v: string) => v && <a href={`mailto:${v}`} onClick={(e) => e.stopPropagation()}>{v}</a> },
    { key: 'whatsappNumber', title: 'WhatsApp', dataIndex: 'whatsappNumber', width: 140, defaultVisible: false,
      render: (v: string) => displayMobile(v) || '' },
    { key: 'onboardedAt', title: 'Onboarded', dataIndex: 'onboardedAt', width: 130, sortable: true, defaultVisible: false, render: (v: string) => formatIstDate(v) },
    {
      key: 'agreement', title: 'Agreement', width: 160, sortable: true,
      sorter: (a: any, b: any) => String(a.agreementExpiry ?? '').localeCompare(String(b.agreementExpiry ?? '')),
      render: (_: any, r: any) => {
        const s = agreementStatus(r.agreementExpiry);
        return (
          <div>
            <Tag tone={s.color}>{s.label}</Tag>
            {r.agreementExpiry && <div style={{ fontSize: 11, color: token.colorTextTertiary }}>till {formatIstDate(r.agreementExpiry)}</div>}
          </div>
        );
      },
    },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', width: 130, sortable: true, alwaysVisible: true, render: (v: string) => formatIstDate(v) },
    {
      key: 'actions', title: 'Actions', width: 180, alwaysVisible: true, fixed: 'right',
      render: (_: any, r: any) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Tooltip label="Open detail"><Button size="sm" variant="text" aria-label="Open detail" icon={<Eye size={16} />} onClick={() => navigate(`/retailers/${r.id}`)} /></Tooltip>
          {canEdit && (<Tooltip label="Edit"><Button size="sm" variant="text" aria-label="Edit" icon={<Pencil size={16} />} onClick={() => { setEditing(r); setFormOpen(true); }} /></Tooltip>)}
          {canStatus && (
            <Popconfirm
              title={r.status === 'ACTIVE' ? 'Deactivate this retailer?' : 'Activate this retailer?'}
              description={r.status === 'ACTIVE' ? 'They will be marked SUSPENDED.' : 'They will be marked ACTIVE.'}
              okText={r.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
              okButtonProps={{ danger: r.status === 'ACTIVE', loading: statusMut.isPending }}
              onConfirm={() => statusMut.mutate({ id: String(r.id), active: r.status !== 'ACTIVE' })}
            >
              <Tooltip label={r.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}>
                <Button size="sm" variant="text"
                  aria-label={r.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                  icon={r.status === 'ACTIVE' ? <Ban size={16} className="text-danger-600 dark:text-danger-500" /> : <CheckCircle2 size={16} />} />
              </Tooltip>
            </Popconfirm>
          )}
          {canMerge && (<Tooltip label="Merge duplicate"><Button size="sm" variant="text" aria-label="Merge duplicate" icon={<Merge size={16} />} onClick={() => setMergingDup(r)} /></Tooltip>)}
          <Tooltip label="Call"><Button size="sm" variant="text" aria-label="Call" icon={<Phone size={16} />} onClick={() => { window.location.href = `tel:${displayMobile(r.mobile)}`; }} /></Tooltip>
          <Tooltip label="WhatsApp">
            <Button size="sm" variant="text" aria-label="WhatsApp" icon={<MessageCircle size={16} />} onClick={() => window.open(`https://wa.me/${waNumber(r.whatsappNumber ?? r.mobile)}`, '_blank', 'noopener')} />
          </Tooltip>
          <Tooltip label="View on map">
            <Button size="sm" variant="text" aria-label="View on map" icon={<MapPin size={16} />} onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${r.latitude},${r.longitude}`, '_blank', 'noopener')} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Retailers</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setFormOpen(true); }}>Add retailer</Button>
          )}
          {canImport && (
            <Button size="sm" icon={<Upload size={16} />} onClick={() => setImportOpen(true)}>Import CSV</Button>
          )}
          {canExport && (
            <Button size="sm" icon={<Download size={16} />} loading={exporting} onClick={exportCsv}>Export CSV</Button>
          )}
          {canImport && <TemplateButton kind="retailers" size="small" />}
        </div>
      </div>
      <Card>
        <Space wrap size={[8, 8]} style={{ marginBottom: 12, width: '100%' }}>
          <Input prefix={<Search size={16} />} placeholder="Search company, contact, mobile, code…" allowClear
            style={{ width: 260 }} value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} />
          <Select mode="multiple" allowClear placeholder="Status" style={{ minWidth: 150 }} maxTagCount={1}
            value={statusF} onChange={(v) => { setStatusF(v); setPage(1); }} options={opt(facets?.status)} />
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Territory" style={{ minWidth: 180 }} maxTagCount={1}
            value={territoryF} onChange={(v) => { setTerritoryF(v); setPage(1); }}
            options={(territories ?? []).map((t: any) => ({ value: String(t.id), label: `${t.code} · ${t.name}` }))} />
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Sales person" style={{ minWidth: 180 }} maxTagCount={1}
            value={salesF} onChange={(v) => { setSalesF(v); setPage(1); }}
            options={(salesUsers?.data ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }))} />
          <Select mode="multiple" allowClear placeholder="State" style={{ minWidth: 150 }} maxTagCount={1}
            value={stateF} onChange={(v) => { setStateF(v); setPage(1); }} options={stateOpts} />
          <Select mode="multiple" allowClear showSearch placeholder="City" style={{ minWidth: 140 }} maxTagCount={1}
            value={cityF} onChange={(v) => { setCityF(v); setPage(1); }} options={opt(facets?.city)} />
          <Select mode="multiple" allowClear placeholder="Credit days" style={{ minWidth: 130 }} maxTagCount={1}
            value={creditF} onChange={(v) => { setCreditF(v); setPage(1); }} options={creditOpts} />
          <Tooltip label="Only retailers with an outstanding balance">
            <Button variant={hasOutstanding ? 'primary' : 'default'} onClick={() => { setHasOutstanding((v) => !v); setPage(1); }}>Outstanding</Button>
          </Tooltip>
          {activeFilters > 0 && (
            <Badge count={activeFilters} size="small"><Button icon={<Eraser size={16} />} onClick={clearFilters}>Clear</Button></Badge>
          )}
        </Space>

        <DataTable
          storageKey="retailers-list"
          rowKey="id"
          dataSource={data?.data ?? []}
          loading={isLoading || isFetching}
          columns={columns}
          onRow={(r) => {
            const ag = agreementStatus(r.agreementExpiry);
            return {
              onClick: () => navigate(`/retailers/${r.id}`),
              style: {
                cursor: 'pointer',
                // Tint the whole row red when the dealer agreement is expiring soon or expired.
                background: ag.alert ? '#fff1f0' : undefined,
              },
            };
          }}
          scroll={{ x: 1300 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.meta?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `${t.toLocaleString('en-IN')} retailers`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      <RetailerFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }} record={editing} />
      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['retailers'] })}
        kind="retailers"
        entityPlural="retailers"
        allowUpsert
        columns="code, companyName, contactPerson, mobile, whatsappNumber, email, gstNumber, panNumber, addressLine1, city, stateCode, pincode, latitude, longitude, territoryCode, creditLimit, creditPeriodDays"
        note={<>Required: <b>companyName, mobile, territoryCode</b>. Leave <b>code</b> blank to auto-assign (R-######), or provide one to keep it. </>}
      />
      {canMerge && <MergeCustomerModal open={!!mergingDup} onClose={() => setMergingDup(null)} customerType="RETAILER" duplicate={mergingDup ?? {}} />}
    </div>
  );
}
